import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

type Phase = 'idle' | 'loading' | 'done';

export function TopProgressBar() {
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const kickoff = setTimeout(() => {
      setPhase('loading');
      setProgress(0);
    }, 0);

    const t1 = setTimeout(() => setProgress(30), 30);
    const t2 = setTimeout(() => setProgress(65), 180);
    const t3 = setTimeout(() => setProgress(85), 380);

    const doneTimeout = setTimeout(() => {
      setPhase('done');
      setProgress(100);
    }, 520);

    const hideTimeout = setTimeout(() => {
      setPhase('idle');
      setProgress(0);
    }, 780);

    return () => {
      clearTimeout(kickoff);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(doneTimeout);
      clearTimeout(hideTimeout);
    };
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {phase !== 'idle' && (
        <motion.div
          key="top-progress"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          className="pointer-events-none fixed inset-x-0 top-0 z-[200] h-[2px] overflow-hidden"
          aria-hidden="true"
        >
          <motion.div
            animate={{ width: `${progress}%` }}
            transition={{
              duration: phase === 'done' ? 0.2 : 0.35,
              ease: phase === 'done' ? 'easeOut' : 'easeInOut',
            }}
            className="relative h-full"
            style={{
              background:
                'linear-gradient(90deg, var(--color-foreground) 0%, var(--color-foreground) 70%, var(--color-foreground-secondary) 100%)',
              boxShadow: '0 0 8px var(--color-foreground)',
            }}
          >
            <span
              className="absolute inset-y-0 right-0 w-16 opacity-70"
              style={{
                background:
                  'linear-gradient(90deg, transparent 0%, var(--color-foreground) 100%)',
                filter: 'blur(4px)',
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TopProgressBar;
